import * as THREE from 'three';
import { brepEdgesGeometry } from '../brepEdges';
import {
  FACE_DEPTH_OFFSET,
  edgeColor,
  featureEdges,
  isModelMesh,
  modelColor,
  modelEdgeLines,
  segmentCount,
  setEdgeStyle,
} from '../viewport';
import type { EdgeLines } from '../viewport';
import { KEEP_NONE, cloneOriginal, disposeMaterials, isSameMaterial, materialList } from './materials';
import type { MaterialSlot } from './materials';

export type DisplayMode = 'solid-edges' | 'shaded' | 'wireframe' | 'ghosted';

const EDGE_OPACITY = 0.42;
const GHOST_OPACITY = 0.3;

// No depth writes, so the far faces show through the near ones.
function applyGhosted(mesh: THREE.Mesh, orig: MaterialSlot): void {
  const restored = cloneOriginal(orig);
  materialList(restored).forEach((mat) => {
    mat.transparent = true;
    mat.opacity = GHOST_OPACITY;
    mat.depthWrite = false;
    mat.side = THREE.DoubleSide;
  });
  mesh.material = restored;
}

// The offset keeps the edges, which run along triangle edges of the faces, from
// z-fighting with them. A sheet has no inside, so its back is seen too.
function applyShadedFaces(mesh: THREE.Mesh, orig: MaterialSlot, showFaces: boolean): void {
  const restored = cloneOriginal(orig);
  materialList(restored).forEach((mat) => {
    mat.side = THREE.DoubleSide;
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = FACE_DEPTH_OFFSET;
    mat.polygonOffsetUnits = FACE_DEPTH_OFFSET;
    mat.visible = showFaces;
  });
  mesh.material = restored;
}

// A smooth mesh has no creases sharp enough to draw, so its triangles stand in.
function applyWireframe(mesh: THREE.Mesh, orig: MaterialSlot): void {
  const restored = cloneOriginal(orig);
  materialList(restored).forEach((mat) => {
    if ('wireframe' in mat) (mat as THREE.MeshStandardMaterial).wireframe = true;
  });
  mesh.material = restored;
}

// Built on first use and kept: extracting them walks every triangle. Wireframe
// draws these same edges rather than the triangles.
function showEdges(
  owner: THREE.Object3D,
  key: string,
  build: () => THREE.BufferGeometry,
  mode: DisplayMode,
  isDark: boolean,
): EdgeLines | undefined {
  let edges = owner.children.find((child) => child.userData[key]) as EdgeLines | undefined;
  const visible = mode === 'solid-edges' || mode === 'wireframe';
  if (!edges && !visible) return undefined;
  if (!edges) {
    edges = modelEdgeLines(build());
    edges.userData[key] = true;
    owner.add(edges);
  }
  const wireframe = mode === 'wireframe';
  edges.visible = visible;
  setEdgeStyle(edges, wireframe ? modelColor(isDark) : edgeColor(isDark), wireframe ? 1 : EDGE_OPACITY);
  return edges;
}

function hasSegments(edges: EdgeLines | undefined): boolean {
  return edges !== undefined && segmentCount(edges) > 0;
}

export function applyDisplayMode(
  object: THREE.Object3D,
  mode: DisplayMode,
  isDark: boolean,
  originalMaterials: Map<string, MaterialSlot>,
  keep = KEEP_NONE,
): void {
  const brep = object.userData.brepFaces === true;
  if (brep) showEdges(object, '__brepEdges', () => brepEdgesGeometry(object), mode, isDark);

  const meshes: THREE.Mesh[] = [];
  object.traverse((child) => {
    if (isModelMesh(child)) meshes.push(child);
  });
  for (const mesh of meshes) {
    const orig = originalMaterials.get(mesh.uuid);
    if (!orig) continue;

    const prev = mesh.material;

    const edges = brep ? undefined : showEdges(mesh, '__featureEdges', () => featureEdges(mesh), mode, isDark);
    if (mode === 'ghosted') applyGhosted(mesh, orig);
    else if (mode === 'wireframe' && !brep && !hasSegments(edges)) applyWireframe(mesh, orig);
    else applyShadedFaces(mesh, orig, mode !== 'wireframe');

    if (!isSameMaterial(prev, orig)) disposeMaterials(materialList(prev).filter((m) => !keep.has(m)));
  }
}
